/**
 * RTF 导出服务
 * 将 Markdown 内容转换为 RTF 格式
 */

import { marked } from 'marked'
import { ExportOptions } from '../../../types/export'
import { downloadBlob } from '../../../utils/download'
import { generateExportFilename } from './exportStyles'

/**
 * 将 Unicode 字符转换为 RTF 转义序列
 */
const unicodeToRtf = (text: string): string => {
  return text.replace(/[\u0080-\uFFFF]/g, (match) => {
    const code = match.charCodeAt(0)
    return `\\u${code}?`
  })
}

/**
 * 转义 RTF 特殊字符
 */
const escapeRtf = (text: string): string => {
  return text
    .replace(/\\/g, '\\\\')  // 反斜杠
    .replace(/{/g, '\\{')    // 左大括号
    .replace(/}/g, '\\}')    // 右大括号
    .replace(/\n/g, '\\line ') // 换行符（使用\\line而不是\\par，更适合代码块）
    .replace(/\t/g, '\\tab ') // 制表符
}

/**
 * 将 HTML 表格转换为 RTF 表格
 */
const htmlTableToRtf = (html: string): string => {
  // 匹配表格标签
  const tableRegex = /<table[^>]*>([\s\S]*?)<\/table>/gi
  
  return html.replace(tableRegex, (match, tableContent) => {
    // 提取表头和数据行
    const rows = tableContent.match(/<tr[^>]*>([\s\S]*?)<\/tr>/gi) || []
    
    if (rows.length === 0) return match
    
    // 解析表格数据
    const tableData: string[][] = []
    
    rows.forEach((row: string) => {
      const cells = row.match(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi) || []
      const rowData = cells.map((cell: string) => {
        // 移除HTML标签，保留纯文本
        return cell.replace(/<[^>]*>/g, '').trim()
      })
      if (rowData.length > 0) {
        tableData.push(rowData)
      }
    })
    
    if (tableData.length === 0) return match
    
    // 计算列数（取最大值）
    const maxCols = Math.max(...tableData.map(row => row.length))
    
    // 构建RTF表格
    let rtfTable = '\\trowd\\trgaph100\\trleft100\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trhdr'
    
    // 定义列宽（平均分配）
    const cellWidth = Math.floor(8000 / maxCols) // RTF单位
    
    for (let i = 0; i < maxCols; i++) {
      rtfTable += `\\clbrdrt\\brdrs\\brdrw10\\clbrdrl\\brdrs\\brdrw10\\clbrdrr\\brdrs\\brdrw10\\clbrdrb\\brdrs\\brdrw10\\cellx${(i + 1) * cellWidth}`
    }
    
    // 添加表格数据
    tableData.forEach((row, rowIndex) => {
      rtfTable += '\\intbl'
      
      row.forEach((cell) => {
        const escapedCell = escapeRtf(cell)
        rtfTable += escapedCell + '\\cell'
      })
      
      // 补充空单元格
      for (let i = row.length; i < maxCols; i++) {
        rtfTable += '\\cell'
      }
      
      rtfTable += '\\row'
      
      // 如果不是最后一行，定义下一行的格式
      if (rowIndex < tableData.length - 1) {
        rtfTable += '\\trowd\\trgaph100\\trleft100\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10'
        
        for (let i = 0; i < maxCols; i++) {
          rtfTable += `\\clbrdrt\\brdrs\\brdrw10\\clbrdrl\\brdrs\\brdrw10\\clbrdrr\\brdrs\\brdrw10\\clbrdrb\\brdrs\\brdrw10\\cellx${(i + 1) * cellWidth}`
        }
      }
    })
    
    rtfTable += '\\pard\\par'
    
    return rtfTable
  })
}

/**
 * 将 HTML 转换为 RTF
 */
const htmlToRtf = (html: string): string => {
  // RTF 头部，支持中文
  let rtf = '{\\rtf1\\ansicpg936\\deff0'
  rtf += '{\\fonttbl{\\f0\\fnil\\fcharset134 SimSun;}{\\f1\\fnil\\fcharset0 Courier New;}}'
  rtf += '{\\colortbl;\\red0\\green0\\blue0;\\red0\\green0\\blue255;\\red255\\green0\\blue0;}'
  rtf += '\\f0\\fs24' // 使用中文字体，12pt
  
  // 首先处理表格（必须在其他处理之前）
  let converted = htmlTableToRtf(html)
  
  // 然后处理其他 HTML 标签 - 注意处理顺序很重要！
  converted = converted
    // 首先处理代码块（必须在单独的代码处理之前）
    .replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, (_match, content) => {
      // 移除内部的<code>标签，只保留纯文本
      const cleanContent = content.replace(/<\/?code[^>]*>/gi, '')
      return `\\f1\\fs20\\pard\\li720 ${escapeRtf(cleanContent)}\\pard\\f0\\fs24\\par\\par`
    })
    // 标题
    .replace(/<h1[^>]*>(.*?)<\/h1>/gi, (_match, content) => {
      return `\\fs32\\b ${escapeRtf(content)}\\b0\\fs24\\par\\par`
    })
    .replace(/<h2[^>]*>(.*?)<\/h2>/gi, (_match, content) => {
      return `\\fs28\\b ${escapeRtf(content)}\\b0\\fs24\\par\\par`
    })
    .replace(/<h3[^>]*>(.*?)<\/h3>/gi, (_match, content) => {
      return `\\fs26\\b ${escapeRtf(content)}\\b0\\fs24\\par\\par`
    })
    // 有序列表和无序列表
    .replace(/<ul[^>]*>/gi, '\\pard\\li720\\fi-720')
    .replace(/<ol[^>]*>/gi, '\\pard\\li720\\fi-720')
    .replace(/<\/ul>|<\/ol>/gi, '\\pard\\par')
    // 列表项
    .replace(/<li[^>]*>(.*?)<\/li>/gi, (_match, content) => {
      return `• ${escapeRtf(content)}\\par`
    })
    // 段落
    .replace(/<p[^>]*>(.*?)<\/p>/gi, (_match, content) => {
      return `\\pard${escapeRtf(content)}\\par\\par`
    })
    // 强调
    .replace(/<strong[^>]*>(.*?)<\/strong>/gi, (_match, content) => {
      return `\\b ${escapeRtf(content)}\\b0`
    })
    .replace(/<em[^>]*>(.*?)<\/em>/gi, (_match, content) => {
      return `\\i ${escapeRtf(content)}\\i0`
    })
    // 单独的代码（不在pre标签内的）
    .replace(/<code[^>]*>(.*?)<\/code>/gi, (_match, content) => {
      return `\\f1\\fs20 ${escapeRtf(content)}\\f0\\fs24`
    })
    // 链接
    .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (_match, href, text) => {
      return `{{\\field{\\*\\fldinst{HYPERLINK "${href}"}}{\\fldrslt{\\ul\\cf1 ${escapeRtf(text)}}}}}`
    })
    // 换行
    .replace(/<br[^>]*>/gi, '\\line')
    // HTML 实体
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // 移除剩余的 HTML 标签
    .replace(/<[^>]*>/g, '')
  
  // 转换 Unicode 字符
  converted = unicodeToRtf(converted)
  
  rtf += converted + '}'
  return rtf
}

/**
 * 导出为 RTF 文件
 */
export const exportToRTF = async (
  content: string,
  options: ExportOptions
): Promise<void> => {
  try {
    // 配置 marked
    marked.setOptions({
      breaks: true,
      gfm: true,
    })
    
    // 转换 Markdown 为 HTML
    let html = await marked.parse(content)
    
    // 添加标题（如果提供了标题）
    if (options.title) {
      html = `<h1>${options.title}</h1>\n${html}`
    }
    
    // 转换为 RTF
    const rtfContent = htmlToRtf(html)
    
    // 创建 blob，使用正确的编码
    const blob = new Blob([rtfContent], { 
      type: 'application/rtf;charset=utf-8' 
    })
    
    // 下载文件
    const filename = generateExportFilename('rtf', options.filename)
    downloadBlob(blob, filename)
    
  } catch (error) {
    console.error('RTF 导出失败:', error)
    throw new Error(`RTF 导出失败: ${error instanceof Error ? error.message : '未知错误'}`)
  }
}

/**
 * 预览 RTF 内容
 */
export const previewRTF = (
  content: string,
  options: ExportOptions
): string => {
  try {
    // 配置 marked
    marked.setOptions({
      breaks: true,
      gfm: true,
    })
    
    // 转换 Markdown 为 HTML
    let html = marked.parse(content) as string
    
    // 添加标题（如果提供了标题）
    if (options.title) {
      html = `<h1>${options.title}</h1>\n${html}`
    }
    
    // 转换为 RTF
    return htmlToRtf(html)
    
  } catch (error) {
    console.error('RTF 预览失败:', error)
    return '预览生成失败'
  }
}

/**
 * 检查浏览器是否支持 RTF 导出
 */
export const checkRTFSupport = (): boolean => {
  // RTF 导出只需要 Blob API 支持
  return typeof Blob !== 'undefined' && typeof URL !== 'undefined'
}